import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useTranslation } from "react-i18next";
import { createCancellationPolicyRuleDraft } from "../utils/spaCancellationPolicy";

function NoticePeriodsEditor({ rule, onChange, disabled, prefix }) {
  const { t } = useTranslation();
  const periods = rule.noticePeriods || [];
  const updatePeriod = (index, patch) => onChange({
    noticePeriods: periods.map((period, itemIndex) => (
      itemIndex === index ? { ...period, ...patch } : period
    )),
  });
  const removePeriod = (index) => onChange({
    noticePeriods: periods.filter((_, itemIndex) => itemIndex !== index),
  });

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" fontWeight={800}>
        {t("spa.settings.noticeIntervals", { defaultValue: "Cancellation intervals" })}
      </Typography>
      {periods.map((period, index) => (
        <Grid container spacing={1} alignItems="center" key={`${prefix}-${index}`}>
          <Grid item xs={5} sm={5}>
            <TextField
              disabled={disabled}
              fullWidth
              size="small"
              type="number"
              label={t("spa.settings.noticeMinutes", { defaultValue: "Minutes before start" })}
              value={period.minimumNoticeMinutes}
              onChange={(event) => updatePeriod(index, { minimumNoticeMinutes: event.target.value })}
              inputProps={{ min: 0, max: 525600, step: 1 }}
            />
          </Grid>
          <Grid item xs={5} sm={5}>
            <TextField
              disabled={disabled}
              fullWidth
              size="small"
              type="number"
              label={t("spa.settings.feePercent", { defaultValue: "Fee %" })}
              value={period.feePercent}
              onChange={(event) => updatePeriod(index, { feePercent: event.target.value })}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
          </Grid>
          <Grid item xs={2} sm={2}>
            <Tooltip title={t("spa.settings.removeInterval", { defaultValue: "Remove interval" })}>
              <span>
                <IconButton
                  disabled={disabled || periods.length === 1}
                  color="error"
                  aria-label={t("spa.settings.removeInterval", { defaultValue: "Remove interval" })}
                  onClick={() => removePeriod(index)}
                >
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      ))}
      <Box>
        <Button
          disabled={disabled || periods.length >= 20}
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onChange({
            noticePeriods: [...periods, { minimumNoticeMinutes: 0, feePercent: 0 }],
          })}
        >
          {t("spa.settings.addInterval", { defaultValue: "Add interval" })}
        </Button>
      </Box>
      <TextField
        disabled={disabled}
        fullWidth
        size="small"
        type="number"
        label={t("spa.settings.noShowFee", { defaultValue: "No-show fee %" })}
        value={rule.noShowFeePercent}
        onChange={(event) => onChange({ noShowFeePercent: event.target.value })}
        inputProps={{ min: 0, max: 100, step: 0.01 }}
      />
    </Stack>
  );
}

export default function SpaCancellationPolicyEditor({
  value,
  onChange,
  treatments = [],
  packages = [],
  disabled = false,
}) {
  const { t } = useTranslation();
  const updateGlobal = (patch) => onChange({
    ...value,
    globalRule: { ...value.globalRule, ...patch },
  });
  const updateTargeted = (index, patch) => onChange({
    ...value,
    targetedRules: value.targetedRules.map((rule, itemIndex) => (
      itemIndex === index ? { ...rule, ...patch } : rule
    )),
  });
  const removeTargeted = (index) => onChange({
    ...value,
    targetedRules: value.targetedRules.filter((_, itemIndex) => itemIndex !== index),
  });

  return (
    <Box>
      <Typography fontWeight={900} mb={0.5}>
        {t("spa.settings.cancellationPolicy", { defaultValue: "Cancellation policy" })}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        {t("spa.settings.cancellationPolicyHelp", {
          defaultValue: "Configure the default policy and optional rules for treatments, packages, members, or customer types.",
        })}
      </Typography>

      <Typography variant="subtitle1" fontWeight={850} mb={1}>
        {t("spa.settings.defaultPolicy", { defaultValue: "Default rule" })}
      </Typography>
      <NoticePeriodsEditor
        rule={value.globalRule}
        onChange={updateGlobal}
        disabled={disabled}
        prefix="global"
      />

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mb={1}>
        <Typography variant="subtitle1" fontWeight={850}>
          {t("spa.settings.targetedPolicies", { defaultValue: "Specific rules" })}
        </Typography>
        <Button
          disabled={disabled || value.targetedRules.length >= 99}
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onChange({
            ...value,
            targetedRules: [...value.targetedRules, createCancellationPolicyRuleDraft()],
          })}
        >
          {t("spa.settings.addPolicy", { defaultValue: "Add rule" })}
        </Button>
      </Stack>

      {value.targetedRules.length === 0 ? (
        <Alert severity="info">
          {t("spa.settings.noTargetedPolicies", { defaultValue: "No specific rules configured." })}
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {value.targetedRules.map((rule, index) => (
            <Paper variant="outlined" sx={{ p: 1.5 }} key={`targeted-rule-${index}`}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={850}>
                    {t("spa.settings.policyNumber", { defaultValue: "Specific rule {{number}}", number: index + 1 })}
                  </Typography>
                  <Tooltip title={t("spa.settings.removePolicy", { defaultValue: "Remove rule" })}>
                    <span>
                      <IconButton
                        disabled={disabled}
                        color="error"
                        aria-label={t("spa.settings.removePolicy", { defaultValue: "Remove rule" })}
                        onClick={() => removeTargeted(index)}
                      >
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <FormControl disabled={disabled} fullWidth size="small">
                      <InputLabel>{t("spa.settings.policyTreatment", { defaultValue: "Treatment" })}</InputLabel>
                      <Select
                        label={t("spa.settings.policyTreatment", { defaultValue: "Treatment" })}
                        value={rule.treatmentProductId}
                        onChange={(event) => updateTargeted(index, { treatmentProductId: event.target.value })}
                      >
                        <MenuItem value="">{t("spa.settings.allTreatments", { defaultValue: "All treatments" })}</MenuItem>
                        {treatments.map((treatment) => (
                          <MenuItem key={treatment.id} value={String(treatment.id)}>{treatment.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl disabled={disabled} fullWidth size="small">
                      <InputLabel>{t("spa.settings.policyPackage", { defaultValue: "Package" })}</InputLabel>
                      <Select
                        label={t("spa.settings.policyPackage", { defaultValue: "Package" })}
                        value={rule.packageId}
                        onChange={(event) => updateTargeted(index, { packageId: event.target.value })}
                      >
                        <MenuItem value="">{t("spa.settings.allPackages", { defaultValue: "All packages" })}</MenuItem>
                        {packages.map((item) => (
                          <MenuItem key={item.id} value={String(item.id)}>{item.name || `Package ${item.id}`}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl disabled={disabled} fullWidth size="small">
                      <InputLabel>{t("spa.settings.policyAudience", { defaultValue: "Customer type" })}</InputLabel>
                      <Select
                        label={t("spa.settings.policyAudience", { defaultValue: "Customer type" })}
                        value={rule.audience}
                        onChange={(event) => updateTargeted(index, { audience: event.target.value })}
                      >
                        <MenuItem value="ALL">{t("spa.settings.allCustomers", { defaultValue: "All customers" })}</MenuItem>
                        <MenuItem value="MEMBER">{t("spa.settings.membersOnly", { defaultValue: "Members only" })}</MenuItem>
                        <MenuItem value="HOTEL_GUEST">{t("spa.customerTypes.hotelGuest", { defaultValue: "Hotel guest" })}</MenuItem>
                        <MenuItem value="EXTERNAL_GUEST">{t("spa.customerTypes.external", { defaultValue: "External guest" })}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <NoticePeriodsEditor
                  rule={rule}
                  onChange={(patch) => updateTargeted(index, patch)}
                  disabled={disabled}
                  prefix={`targeted-${index}`}
                />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
}
